import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const QKEY = "onkaal-tech-queue";
const CKEY = "onkaal-tech-cache:";
const EVT = "onkaal-tech-queue-change";

const readQ = () => { try { return JSON.parse(localStorage.getItem(QKEY) || "[]"); } catch { return []; } };
const writeQ = (q) => { localStorage.setItem(QKEY, JSON.stringify(q)); window.dispatchEvent(new Event(EVT)); };
const isNetworkError = (e) => !e?.response;

// GET with last-known-good cache so jobs stay readable offline.
export const cachedGet = async (url) => {
  try {
    const { data } = await api.get(url);
    try { localStorage.setItem(CKEY + url, JSON.stringify(data)); } catch { /* storage full */ }
    return data;
  } catch (e) {
    const hit = localStorage.getItem(CKEY + url);
    if (isNetworkError(e) && hit) return JSON.parse(hit);
    throw e;
  }
};

export const patchCache = (url, fn) => {
  const hit = localStorage.getItem(CKEY + url);
  if (hit) localStorage.setItem(CKEY + url, JSON.stringify(fn(JSON.parse(hit))));
};

// Sends now if online; otherwise saves to the queue and returns { queued: true }.
export const techSend = async (method, url, body, label) => {
  if (navigator.onLine) {
    try {
      return { data: (await api({ method, url, data: body })).data };
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  writeQ([...readQ(), { id: `${Date.now()}-${Math.random()}`, method, url, body, label, at: new Date().toISOString() }]);
  return { queued: true };
};

let flushing = false;
export const flushQueue = async (onDone) => {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  let synced = 0;
  try {
    let q = readQ();
    while (q.length) {
      const item = q[0];
      try {
        await api({ method: item.method, url: item.url, data: item.body });
        synced += 1;
      } catch (e) {
        const st = e?.response?.status;
        if (isNetworkError(e) || st >= 500) break;
        if (st === 401) {
          toast.error("Session expired — log in again to sync your saved updates");
          break;
        }
        toast.error(`Could not sync "${item.label}": ${errMsg(e)}`);
      }
      q = readQ().filter((x) => x.id !== item.id);
      writeQ(q);
    }
  } finally {
    flushing = false;
    if (synced) toast.success(`${synced} offline update(s) synced`);
    onDone?.();
  }
};

export const useOffline = () => {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(readQ().length);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    const q = () => setPending(readQ().length);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener(EVT, q);
    window.addEventListener("storage", q);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener(EVT, q);
      window.removeEventListener("storage", q);
    };
  }, []);
  return { online, pending };
};
